import React from "react";
import { ChipProps, ChipGroupProps } from "../../types/chip-types";
import { useChip } from "../../hooks/useChip";

export const Chip: React.FC<ChipProps> = ({
  label,
  selected = false,
  disabled = false,
  variant = "default",
  onClick,
  onDelete,
  style = {},
  className = "",
  icon,
}) => {
  const { chipStyle, handleMouseEnter, handleMouseLeave } = useChip({
    selected,
    disabled,
    variant,
    style,
  });

  return (
    <div
      style={chipStyle}
      className={className}
      onClick={!disabled ? onClick : undefined}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {icon && (
        <span
          style={{ marginRight: "6px", display: "flex", alignItems: "center" }}
        >
          {icon}
        </span>
      )}
      {label}
      {onDelete && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          style={{
            marginLeft: "8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            opacity: 0.6,
          }}
        >
          ✕
        </span>
      )}
    </div>
  );
};

export const ChipGroup: React.FC<ChipGroupProps> = ({
  options,
  value,
  onChange,
  multiple = false,
  onAddCustom,
  disabled = false,
  style = {},
  chipStyle = {},
  className = "",
  label,
  labelStyle = {},
}) => {
  const isSelected = (optionValue: string | number) => {
    if (multiple && Array.isArray(value)) {
      return value.includes(optionValue);
    }
    return value === optionValue;
  };

  const handleChipClick = (optionValue: string | number) => {
    if (disabled) return;

    if (multiple) {
      const currentValues = Array.isArray(value) ? [...value] : [];
      const index = currentValues.indexOf(optionValue);

      if (index === -1) {
        onChange?.([...currentValues, optionValue]);
      } else {
        onChange?.(currentValues.filter((v) => v !== optionValue));
      }
    } else {
      onChange?.(optionValue);
    }
  };

  return (
    <div className={className} style={{ width: "100%" }}>
      {label && (
        <label
          style={{
            display: "block",
            fontSize: "14px",
            fontWeight: "500",
            color: "#374151",
            marginBottom: "8px",
            ...labelStyle,
          }}
        >
          {label}
        </label>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {options.map((option) => (
          <Chip
            key={option.value}
            label={option.label}
            value={option.value}
            selected={isSelected(option.value)}
            disabled={disabled}
            onClick={() => handleChipClick(option.value)}
            style={chipStyle}
          />
        ))}
        {onAddCustom && (
          <Chip
            label="+ Custom"
            variant="dashed"
            onClick={onAddCustom}
            disabled={disabled}
            style={chipStyle}
          />
        )}
      </div>
    </div>
  );
};

export default Chip;
